/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { isEmpty, isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useRouter } from '~shared/components/hoc/withRouter';
import { useStandardExperienceModeQuery } from '~sq-server-commons/queries/mode';
import {
  SandboxInstanceSettings,
  SandboxSettingsBase,
  SandboxSettingsFormState,
  SandboxSettingsIssueTypes,
  SandboxSettingsSoftwareQualities,
  SandboxSettingsValidationState,
  SandboxSettingValidationState,
} from '../../types';
import { hasAddedCriticalTypes } from '../../utils';

type FormSettings = SandboxSettingsBase &
  Partial<Pick<SandboxInstanceSettings, 'allowOverride' | 'defaultValue'>>;

export function useSandboxSettingForm(settings?: FormSettings) {
  const { query } = useLocation();
  const router = useRouter();
  const location = useLocation();
  const isInstanceSettings = typeof settings?.allowOverride === 'boolean';

  const { data: isStandardMode } = useStandardExperienceModeQuery();
  const initialState = useMemo(() => getInitialFormState(settings), [settings]);

  const [formState, setFormState] = useState<SandboxSettingsFormState>({
    ...initialState,
    // Enable sandbox if sandbox=enable is present in the URL (comes from Intro dialog)
    enabled: initialState.enabled || query.sandbox === 'enable',
  });
  const [validationState, setValidationState] = useState<SandboxSettingsValidationState>(
    getInitialValidationState(),
  );

  const isChanged = useMemo(() => !isEqual(formState, initialState), [formState, initialState]);

  const hasAddedCriticalTypesFlag = useMemo(() => {
    if (!settings || !formState.enabled) {
      return false;
    }

    return hasAddedCriticalTypes(
      formState.softwareQualities,
      settings.softwareQualities,
      formState.types,
      settings.types,
    );
  }, [formState.softwareQualities, formState.types, settings, formState.enabled]);

  const setEnabled = useCallback((enabled: boolean) => {
    setFormState((prev) => ({ ...prev, enabled }));
    if (!enabled) {
      setValidationState(getInitialValidationState());
    }
  }, []);

  const setSoftwareQualities = useCallback(
    (softwareQualities: SandboxSettingsSoftwareQualities) => {
      setFormState((prev) => ({ ...prev, softwareQualities }));
    },
    [],
  );

  const setTypes = useCallback((types: SandboxSettingsIssueTypes) => {
    setFormState((prev) => ({ ...prev, types }));
  }, []);

  const setAllowOverride = useCallback((allowOverride: boolean) => {
    setFormState((prev) => ({ ...prev, allowOverride }));
  }, []);

  const setDefaultValue = useCallback((defaultValue: boolean | null) => {
    setFormState((prev) => ({ ...prev, defaultValue }));
  }, []);

  const validate = useCallback(() => {
    // If form is disabled, skip validation
    if (!formState.enabled) {
      setValidationState(getInitialValidationState());
      return Promise.resolve();
    }

    const validations: Array<{ field: keyof SandboxSettingsFormState; isValid: boolean }> = [
      { field: 'enabled', isValid: true }, // Always valid
      {
        field: 'allowOverride',
        isValid: true,
      },
      {
        field: 'defaultValue',
        isValid: isInstanceSettings ? validateRequiredBoolean(formState.defaultValue) : true,
      },
      {
        field: 'softwareQualities',
        isValid:
          isStandardMode ||
          validateTypesWithSeverities(formState.softwareQualities, 'impactSeverities'),
      },
      {
        field: 'types',
        isValid: !isStandardMode || validateTypesWithSeverities(formState.types, 'severities'),
      },
    ];

    const newValidationState = validations.reduce((acc, { field, isValid }) => {
      acc[field] = isValid
        ? SandboxSettingValidationState.None
        : SandboxSettingValidationState.Invalid;
      return acc;
    }, {} as SandboxSettingsValidationState);

    setValidationState(newValidationState);

    const hasErrors = validations.some(({ isValid }) => !isValid);
    return hasErrors ? Promise.reject(new Error('Validation failed')) : Promise.resolve();
  }, [formState, isStandardMode, isInstanceSettings]);

  const reset = useCallback(() => {
    setFormState(initialState);
    setValidationState(getInitialValidationState());
  }, [initialState]);

  // On api settings change, update form state
  useEffect(() => {
    if (initialState) {
      setFormState({
        ...initialState,
        // Enable sandbox if sandbox=enable is present in the URL (comes from Intro dialog)
        enabled: initialState.enabled || query.sandbox === 'enable',
      });

      setValidationState(getInitialValidationState());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialState, reset]);

  // Remove sandbox=enable from URL when sandbox is enabled
  useEffect(() => {
    if (formState.enabled && query.sandbox === 'enable') {
      router.replace({ query: { ...location.query, sandbox: undefined }, hash: location.hash });
    }
  }, [formState.enabled, query.sandbox, router, location.query, location.hash]);

  return {
    isChanged,
    reset,
    validate,
    formState,
    validationState,
    setEnabled,
    setSoftwareQualities,
    setTypes,
    setAllowOverride,
    setDefaultValue,
    hasAddedCriticalTypesFlag,
  };
}

const getInitialFormState = (settings?: FormSettings): SandboxSettingsFormState => {
  return {
    allowOverride: settings?.enabled ? Boolean(settings?.allowOverride) : false,
    defaultValue: settings?.enabled ? Boolean(settings?.defaultValue) : null,
    enabled: settings?.enabled ?? false,
    softwareQualities: settings?.enabled ? (settings?.softwareQualities ?? []) : [],
    types: settings?.enabled ? (settings?.types ?? []) : [],
  };
};

const getInitialValidationState = (): SandboxSettingsValidationState => {
  return {
    allowOverride: SandboxSettingValidationState.None,
    defaultValue: SandboxSettingValidationState.None,
    enabled: SandboxSettingValidationState.None,
    softwareQualities: SandboxSettingValidationState.None,
    types: SandboxSettingValidationState.None,
  };
};

// Helper validation functions
const validateRequiredBoolean = (value: boolean | null) => value !== null;

const validateTypesWithSeverities = <T, K extends keyof T>(items: T[], severityKey: K): boolean =>
  !isEmpty(items) &&
  items.every((item) => {
    const severities = item[severityKey];
    return Array.isArray(severities) && !isEmpty(severities);
  });
